import { FormEvent, type MouseEventHandler, type ReactNode, useCallback, useEffect, useRef, useState } from "react"
import { ArrowUpRightIcon, Clock3Icon, HeartIcon, ListIcon, MapPinIcon, SearchIcon, ShoppingBagIcon, UserRoundIcon } from "lucide-react"
import { observer } from "mobx-react-lite"
import { useMediaQuery } from "react-responsive"
import { useNavigate } from "react-router"

import Logo from "@/assets/KC_logo_2008.webp"
import { Badge } from "@/components/ui/shadcn/badge"
import { Button } from "@/components/ui/shadcn/button"
import { Input } from "@/components/ui/shadcn/input"
import AuthDialog from "@/features/auth/AuthDialog"
import { CatalogMenu } from "@/features/catalog-menu"
import { csspCartStore } from "@/features/cssp/cart/csspCartStorage"
import { catalogSearchHistory } from "@/features/product-search/catalogSearchHistory"
import basketStore from "@/pages/BasketPage/store/basketStore"
import appStore from "@/stores/appStore"

import CitySelectorDialog from "./CitySelectorDialog"

const StoreHeader = () => {
  const isMobile = useMediaQuery({ maxWidth: 767 })
  const [isCityDialogOpen, setIsCityDialogOpen] = useState(() => !appStore.selectedCity)
  const [isCitiesLoading, setIsCitiesLoading] = useState(true)
  const [hasCitiesLoadError, setHasCitiesLoadError] = useState(false)
  const [search, setSearch] = useState("")
  const [recentSearches, setRecentSearches] = useState(() => catalogSearchHistory.getAll())
  const [isSearchExpansionOpen, setIsSearchExpansionOpen] = useState(false)
  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false)
  const searchExpansionRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const selectedCity = appStore.selectedCity
  const cartCount = basketStore.basket.length + csspCartStore.items.length
  const accountName = appStore.isLoginIn ? appStore.userInfo.name?.trim() : ""
  const accountLabel = accountName || "Аккаунт"

  const submitSearch = (rawQuery: string) => {
    const query = rawQuery.trim()
    if (!query) {
      return
    }

    setRecentSearches(catalogSearchHistory.save(query))
    setIsSearchExpansionOpen(false)
    navigate(`/search?search=${encodeURIComponent(query)}`)
  }

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    submitSearch(search)
  }

  const handleAccountClick: MouseEventHandler<HTMLAnchorElement> = (event) => {
    event.preventDefault()
    if (!appStore.isLoginIn) {
      setIsAuthDialogOpen(true)
      return
    }
    navigate("/profile")
  }

  const loadCities = useCallback(async () => {
    setIsCitiesLoading(true)
    setHasCitiesLoadError(false)

    try {
      await appStore.getCities()
    } catch {
      setHasCitiesLoadError(true)
    } finally {
      setIsCitiesLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isMobile) return
    void loadCities()
  }, [isMobile, loadCities])

  useEffect(() => {
    if (!isMobile && !selectedCity) {
      setIsCityDialogOpen(true)
    }
  }, [isMobile, selectedCity])

  useEffect(() => {
    if (!isSearchExpansionOpen) return

    const handlePointerDown = (event: PointerEvent) => {
      if (!searchExpansionRef.current?.contains(event.target as Node)) setIsSearchExpansionOpen(false)
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsSearchExpansionOpen(false)
    }

    document.addEventListener("pointerdown", handlePointerDown)
    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown)
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [isSearchExpansionOpen])

  if (isMobile) return null

  return (
    <header data-scaena-ui className="relative md:sticky md:top-0 md:z-50">
      <div className="bg-foreground text-white">
        <div className="mx-auto flex h-9 w-full max-w-[1440px] items-center px-4 text-xs font-[550] sm:px-6 xl:px-[30px]">
          <button type="button" className="flex items-center gap-2 rounded-full outline-none transition-colors hover:text-white/82 focus-visible:ring-3 focus-visible:ring-white/20" data-slot="header-city" onClick={() => setIsCityDialogOpen(true)}>
            <MapPinIcon className="size-4" />
            <span>{selectedCity || "Выберите город"}</span>
          </button>
          <nav className="ml-auto hidden items-center gap-6 sm:flex" aria-label="Информация">
            <a href="/about">Доставка и оплата</a>
            <a href="/about">О компании</a>
          </nav>
        </div>
      </div>

      <div className="relative border-b border-white/90 shadow-[var(--scaena-shadow-glass)]">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-white/76 backdrop-blur-[40px]" />
        <div className="relative mx-auto flex min-h-[82px] w-full max-w-[1440px] items-center gap-4 px-4 sm:px-6 xl:gap-5 xl:px-[30px]">
          <a href="/" className="shrink-0" aria-label="КЦ Авто — главная">
            <img src={Logo} alt="КЦ Авто" className="h-10 w-auto sm:h-12" style={{ width: "auto" }} />
          </a>

          <CatalogMenu
            trigger={
              <Button size="lg" className="hidden min-w-[132px] lg:inline-flex">
                <ListIcon data-icon="inline-start" />
                Каталог
              </Button>
            }
          />
          <Button size="icon" aria-label="Каталог" className="lg:hidden">
            <ListIcon />
          </Button>

          <div ref={searchExpansionRef} className="relative hidden min-w-0 flex-1 md:block">
            <form onSubmit={handleSearch} className="relative flex items-center" role="search">
              <div className="flex-1">
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  onFocus={() => {
                    setRecentSearches(catalogSearchHistory.getAll())
                    setIsSearchExpansionOpen(true)
                  }}
                  autoComplete="off"
                  aria-label="Поиск товаров"
                  aria-controls="header-search-expansion"
                  aria-expanded={isSearchExpansionOpen}
                  placeholder="Название запчасти, артикул или марка"
                  className="h-[54px] border-[var(--scaena-control-border)] bg-white/72 pl-5 pr-[138px] shadow-[var(--scaena-shadow-glass)] backdrop-blur-[32px]"
                />
              </div>
              <Button type="submit" data-motion="static" className="absolute right-0.5 top-1/2 h-[50px] min-w-[132px] -translate-y-1/2 rounded-full hover:-translate-y-1/2 active:-translate-y-1/2">
                <SearchIcon data-icon="inline-start" />
                Найти
              </Button>
            </form>
            {isSearchExpansionOpen ? (
              <section id="header-search-expansion" aria-label="Быстрый поиск" className="absolute left-0 top-[calc(100%+10px)] z-50 flex w-[min(640px,calc(100vw-48px))] flex-col gap-3 rounded-[var(--scaena-radius-surface)] border border-[var(--scaena-glass-border)] bg-popover p-3 text-sm text-popover-foreground [box-shadow:var(--scaena-shadow-bento)] backdrop-blur-[48px]">
              {recentSearches.length > 0 ? (
                <section aria-labelledby="recent-searches-title" className="flex flex-col gap-1">
                  <p id="recent-searches-title" className="px-2 pt-1 text-xs font-[550] text-muted-foreground">Недавние запросы</p>
                  {recentSearches.map((query) => (
                    <Button key={query.toLocaleLowerCase("ru-RU")} type="button" variant="ghost" className="justify-start" onClick={() => submitSearch(query)}>
                      <Clock3Icon data-icon="inline-start" />
                      <span className="truncate">{query}</span>
                    </Button>
                  ))}
                </section>
              ) : null}
              <div className={recentSearches.length > 0 ? "flex justify-end border-t border-[var(--scaena-control-border)] pt-2" : "flex justify-end"}>
                <Button type="button" variant="ghost" size="sm" onClick={() => navigate("/ssp")}>
                  Расширенный поиск
                  <ArrowUpRightIcon data-icon="inline-end" />
                </Button>
              </div>
              </section>
            ) : null}
          </div>

          <nav className="ml-auto flex items-center gap-1 sm:gap-2" aria-label="Покупки и аккаунт">
            <HeaderAction href="/profile/favorites" label="Избранное" icon={<HeartIcon />} />
            <HeaderAction href="/profile" label={accountLabel} icon={<UserRoundIcon />} onClick={handleAccountClick} />
            <HeaderAction href="/cart" label="Корзина" icon={<ShoppingBagIcon />} count={cartCount || undefined} />
          </nav>
        </div>
      </div>

      <CitySelectorDialog
        open={isCityDialogOpen}
        selectedCity={selectedCity}
        isLoading={isCitiesLoading}
        hasLoadError={hasCitiesLoadError}
        onOpenChange={setIsCityDialogOpen}
        onRetry={loadCities}
      />
      <AuthDialog
        open={isAuthDialogOpen}
        onOpenChange={setIsAuthDialogOpen}
        onAuthSuccess={(data) => {
          appStore.onAuthCompleted(data)
          navigate("/", { replace: true })
        }}
      />
    </header>
  )
}

const HeaderAction = ({ href, label, icon, count, onClick }: { href: string; label: string; icon: ReactNode; count?: number; onClick?: MouseEventHandler<HTMLAnchorElement> }) => (
  <a href={href} onClick={onClick} className="relative flex min-w-11 flex-col items-center gap-1 rounded-[var(--scaena-radius-compact)] px-2 py-2 text-xs font-[550] text-foreground/78 transition-colors hover:bg-primary/8 hover:text-primary">
    {icon}
    <span className="hidden xl:inline">{label}</span>
    {count ? <Badge className="absolute right-0 top-0 min-h-4 px-1 py-0 text-[10px]">{count}</Badge> : null}
  </a>
)

const ObservedStoreHeader = observer(StoreHeader)

export default ObservedStoreHeader
